import { Flex, Text, TypeTextConfig } from '@bitrise/bitkit';
import React, { FunctionComponent } from 'react';
import './BuildSuccessRate.css';

export interface Props {
  radius: number;
  strokeWidthInner?: number;
  strokeWidthOuter?: number;
  textConfig?: TypeTextConfig;
  totalBuilds: number;
  totalBuildsSuccess: number;
}

interface CircleConfig {
  center?: number;
  gap: number;
  offset?: number;
  percent?: number;
  radius: number;
  strokeWidth: number;
}

interface CircleProps {
  cx: number;
  cy: number;
  r: number;
  strokeDasharray: string;
  strokeWidth: number;
  style: {
    strokeDashoffset: number;
    transform: string;
    transformOrigin: string;
  };
}

const getCircleProps = ({
  radius,
  center = radius,
  gap,
  percent = 1,
  strokeWidth,
}: CircleConfig): CircleProps => {
  const circumference = 2 * (radius - strokeWidth / 2) * Math.PI;
  const circumferenceTotal = circumference * gap;
  const circumferencePercentTotal = circumference - circumferenceTotal;
  const circumferencePercent = circumferencePercentTotal * (1 - percent);

  return {
    cx: center,
    cy: center,
    r: radius - strokeWidth / 2,
    strokeDasharray: `${circumference} ${circumference}`,
    strokeWidth,
    style: {
      strokeDashoffset: circumferenceTotal + circumferencePercent,
      transform: `rotate(${Math.PI / 2 + Math.PI * 2 * gap * 0.5}rad)`,
      transformOrigin: `${center}px ${center}px`,
    },
  };
};

const BuildSuccessRate: FunctionComponent<Props> = ({
  radius,
  strokeWidthInner = 8,
  strokeWidthOuter = 15,
  textConfig = '4',
  totalBuilds,
  totalBuildsSuccess,
}) => {
  const percent = totalBuildsSuccess / totalBuilds || 0;

  const circleTrackProps = getCircleProps({
    gap: 0.15,
    radius,
    strokeWidth: strokeWidthOuter,
  });

  const circleProgressProps = getCircleProps({
    center: radius,
    gap: 0.16,
    percent,
    radius: radius - strokeWidthOuter,
    strokeWidth: strokeWidthInner,
  });

  return (
    <Flex
      container
      height={`${radius * 2}px`}
      maxWidth={`${radius * 2}px`}
      width={`${radius * 2}px`}
    >
      <Flex
        Component="svg"
        className="BuildSuccessRate"
        height={`${radius * 2}px`}
        viewBox={`0 0 ${radius * 2} ${radius * 2}`}
        width={`${radius * 2}px`}
      >
        <circle {...circleTrackProps} className="BuildSuccessRate__track" />
        <circle {...circleProgressProps} className="BuildSuccessRate__progress" />
      </Flex>

      <Flex absolute="center">
        <Text
          align="middle"
          className="BuildSuccessRate__text"
          config={textConfig}
          style={{ '--percent': Math.round(percent * 100) }}
        />
      </Flex>
    </Flex>
  );
};

export default BuildSuccessRate;
